import { z } from 'zod';

const DocumentTypeSchema = z.enum([
  'VEHICLE_REGISTRATION',
  'DRIVING_LICENSE',
  'INSURANCE_POLICY',
]);

const ReviewStateSchema = z.enum([
  'SUBMITTED',
  'PENDING_REVIEW',
  'APPROVED',
  'REJECTED',
]);

const VehicleClassSchema = z.enum(['CAR', 'MOTO']);

const LicenseClassSchema = z.enum([
  'SMALL_CAR',
  'BIG_TRUCK',
  'BIG_BUS',
  'TRAILER',
  'SMALL_CAR_PROFESSIONAL',
  'BIG_TRUCK_PROFESSIONAL',
  'BIG_BUS_PROFESSIONAL',
  'TRAILER_PROFESSIONAL',
  'INTERNATIONAL',
  'MILITARY',
  'SMALL_LIGHT_MOTORCYCLE',
  'ORDINARY_LIGHT_MOTORCYCLE',
  'ORDINARY_HEAVY_MOTORCYCLE',
  'LARGE_HEAVY_MOTORCYCLE',
]);

const VehicleRegistrationFieldsSchema = z.object({
  plateNumber: z.string(),
  vehicleClass: VehicleClassSchema,
  ownerName: z.string(),
  ownerAddress: z.string(),
  printNo: z.string().nullable(),
  brandModel: z.string().nullable(),
  vin: z.string().nullable(),
  color: z.string().nullable(),
  displacementCc: z.number().nullable(),
  manufactureYearMonth: z.string().nullable(),
  issueDate: z.string().nullable(),
  reissueDate: z.string().nullable(),
});

const VehicleRegistrationInfoSchema = VehicleRegistrationFieldsSchema.partial();

const DrivingLicenseFieldsSchema = z.object({
  name: z.string(),
  gender: z.enum(['MALE', 'FEMALE']),
  licenseClass: LicenseClassSchema,
  nationalId: z.string(),
  birthDate: z.string(),
  address: z.string(),
  printNo: z.string().nullable(),
  validUntil: z.string().nullable(),
  issueDate: z.string().nullable(),
});

const DrivingLicenseInfoSchema = DrivingLicenseFieldsSchema.partial();

const InsurancePolicyTypeSchema = z.enum(['COMPULSORY', 'VOLUNTARY']);

const InsurancePolicyEntryFieldsSchema = z.object({
  plateNumber: z.string(),
  policyType: InsurancePolicyTypeSchema,
  expiryDate: z.string(),
  insurer: z.string().nullable(),
});

const InsurancePolicyEntrySchema = InsurancePolicyEntryFieldsSchema.partial();

const InsurancePolicyInfoSchema = z.object({
  entries: z.array(InsurancePolicyEntrySchema),
});

const AccountDocumentFileSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  contentType: z.string(),
  pageOrder: z.number(),
  tags: z.array(z.string()).default([]),
  scanState: z.enum(['PENDING', 'CLEAN', 'INFECTED', 'ERROR']),
  viewUrl: z.string().optional(),
  viewUrlExpireDateTime: z.string().optional(),
});

const AccountDocumentFileViewUrlSchema = z.object({
  viewUrl: z.string(),
  viewUrlExpireDateTime: z.string(),
  fileName: z.string(),
  contentType: z.string(),
});

const AccountDocumentAccountSchema = z.object({
  id: z.string(),
  email: z.string(),
});

const PageExtraSchema = z.object({
  totalElement: z.number(),
  totalPage: z.number(),
});

const AccountDocumentSummaryItemSchema = z.object({
  id: z.string(),
  account: AccountDocumentAccountSchema,
  documentType: DocumentTypeSchema,
  reviewState: ReviewStateSchema,
  revisionNo: z.number(),
  uploadDateTime: z.string(),
  reviewDateTime: z.string().optional(),
  rejectReason: z.string().optional(),
});

const AccountDocumentSchema = z.object({
  id: z.string(),
  account: AccountDocumentAccountSchema,
  documentType: DocumentTypeSchema,
  reviewState: ReviewStateSchema,
  revisionNo: z.number(),
  uploadDateTime: z.string(),
  reviewRecord: z
    .object({
      operator: z.string(),
      reviewDateTime: z.string(),
      rejectReason: z.string().optional(),
    })
    .optional(),
  files: z.array(AccountDocumentFileSchema),
  vehicleRegistrationInfo: VehicleRegistrationInfoSchema.optional(),
  drivingLicenseInfo: DrivingLicenseInfoSchema.optional(),
  insurancePolicyInfo: InsurancePolicyInfoSchema.optional(),
});

const ApproveVehicleRegistrationInfoSchema =
  VehicleRegistrationFieldsSchema.pick({
    plateNumber: true,
    vehicleClass: true,
    ownerName: true,
    ownerAddress: true,
  }).extend({
    printNo: z.string().nullable().optional(),
    brandModel: z.string().nullable().optional(),
    vin: z.string().nullable().optional(),
    color: z.string().nullable().optional(),
    displacementCc: z.number().nullable().optional(),
    manufactureYearMonth: z.string().nullable().optional(),
    issueDate: z.string().nullable().optional(),
    reissueDate: z.string().nullable().optional(),
  });

const ApproveDrivingLicenseInfoSchema = DrivingLicenseFieldsSchema.pick({
  name: true,
  gender: true,
  licenseClass: true,
  nationalId: true,
  birthDate: true,
  address: true,
}).extend({
  printNo: z.string().nullable().optional(),
  validUntil: z.string().nullable().optional(),
  issueDate: z.string().nullable().optional(),
});

const ApproveInsurancePolicyEntrySchema = InsurancePolicyEntryFieldsSchema.pick(
  {
    plateNumber: true,
    policyType: true,
    expiryDate: true,
  },
).extend({
  insurer: z.string().nullable().optional(),
});

const ApproveInsurancePolicyInfoSchema = z.object({
  entries: z.array(ApproveInsurancePolicyEntrySchema).min(1),
});

type ReviewState = z.infer<typeof ReviewStateSchema>;
type DocumentType = z.infer<typeof DocumentTypeSchema>;
type InsurancePolicyType = z.infer<typeof InsurancePolicyTypeSchema>;
type VehicleClass = z.infer<typeof VehicleClassSchema>;
type LicenseClass = z.infer<typeof LicenseClassSchema>;
type AccountDocument = z.infer<typeof AccountDocumentSchema>;
type AccountDocumentFile = z.infer<typeof AccountDocumentFileSchema>;
type AccountDocumentSummaryItem = z.infer<
  typeof AccountDocumentSummaryItemSchema
>;
type VehicleRegistrationInfo = z.infer<typeof VehicleRegistrationInfoSchema>;
type DrivingLicenseInfo = z.infer<typeof DrivingLicenseInfoSchema>;
type InsurancePolicyInfo = z.infer<typeof InsurancePolicyInfoSchema>;
type InsurancePolicyEntry = z.infer<typeof InsurancePolicyEntrySchema>;
type ApproveVehicleRegistrationInfo = z.infer<
  typeof ApproveVehicleRegistrationInfoSchema
>;
type ApproveDrivingLicenseInfo = z.infer<
  typeof ApproveDrivingLicenseInfoSchema
>;
type ApproveInsurancePolicyInfo = z.infer<
  typeof ApproveInsurancePolicyInfoSchema
>;

type ApproveAccountDocumentParams =
  | {
      documentType: 'VEHICLE_REGISTRATION';
      info: ApproveVehicleRegistrationInfo;
    }
  | {
      documentType: 'DRIVING_LICENSE';
      info: ApproveDrivingLicenseInfo;
    }
  | {
      documentType: 'INSURANCE_POLICY';
      info: ApproveInsurancePolicyInfo;
    };

export {
  AccountDocumentSchema,
  AccountDocumentFileSchema,
  AccountDocumentFileViewUrlSchema,
  AccountDocumentSummaryItemSchema,
  ApproveDrivingLicenseInfoSchema,
  ApproveInsurancePolicyInfoSchema,
  ApproveVehicleRegistrationInfoSchema,
  DocumentTypeSchema,
  DrivingLicenseInfoSchema,
  InsurancePolicyEntrySchema,
  InsurancePolicyInfoSchema,
  InsurancePolicyTypeSchema,
  LicenseClassSchema,
  PageExtraSchema,
  ReviewStateSchema,
  VehicleClassSchema,
  VehicleRegistrationInfoSchema,
};

export type {
  AccountDocument,
  AccountDocumentFile,
  ReviewState,
  AccountDocumentSummaryItem,
  ApproveAccountDocumentParams,
  ApproveDrivingLicenseInfo,
  ApproveInsurancePolicyInfo,
  ApproveVehicleRegistrationInfo,
  DocumentType,
  DrivingLicenseInfo,
  InsurancePolicyEntry,
  InsurancePolicyInfo,
  InsurancePolicyType,
  LicenseClass,
  VehicleClass,
  VehicleRegistrationInfo,
};
